import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveDirective as _resolveDirective, createElementBlock as _createElementBlock, withDirectives as _withDirectives, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "text-xl font-semibold text-gray-800" }
const _hoisted_2 = { class: "flex items-center space-x-4" }
const _hoisted_3 = {
  key: 0,
  class: "grid grid-cols-1 gap-4 md:grid-cols-4"
}
const _hoisted_4 = { class: "mb-2 block text-sm font-medium text-gray-800" }
const _hoisted_5 = { class: "mb-2 block text-sm font-medium text-gray-800" }
const _hoisted_6 = { class: "mt-3 flex items-center gap-2" }
const _hoisted_7 = { class: "text-gray-400 transition-colors duration-150 hover:text-gray-600" }

import { useVuelidate, Validation, ValidationArgs } from '@vuelidate/core';
import { maxLength, required } from '@vuelidate/validators';
import { computed, onMounted, reactive, ref, Ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { QuestionMarkCircleIcon } from '@heroicons/vue/outline';

import { goBack } from '@/composables/useNavigation';
import { NumberService, SelectOptionsService, WhatsappCodeService } from '@/services';
import { toast, toastServiceError } from '@/services/core/notification';
import { useUiStore, useAuthStore } from '@/stores';
import { applyPhoneMask } from '@/utils/number';

import {
    DefaultServiceResult,
    NumberFormState,
    NumberModel,
    Permission,
    SelectOptionsServiceGetLevelsResult,
} from '@/types';
import SVGWhatsApp from '@/components/svg/SVGWhatsApp.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'NumberFormPage',
  setup(__props) {

const formState = reactive<NumberFormState>({
    levelId: undefined,
    connectFlowId: undefined,
    mediaLabel: undefined,
});
const number = ref<NumberModel>();
const levelOptions: Ref<SelectOptionsServiceGetLevelsResult[]> = ref([]);
const connectFlowOptions: Ref<Array<{ id: number; name: string }>> = ref([]);
const receiveWhatsappCode = ref(false);
const isInitialLoad = ref(true);

const rules = computed(() => ({
    levelId: { required },
    connectFlowId: {},
    mediaLabel: { maxLength: maxLength(250) },
}));

const route = useRoute();
const uiStore = useUiStore();
const authStore = useAuthStore();
const v$: Ref<Validation<ValidationArgs<NumberFormState>>> = useVuelidate(rules, formState);
const canAssignWhatsappCode = computed(() =>
    authStore.userPermissions.includes(Permission.ASSIGN_WHATSAPP_CODE_NUMBER),
);

watch(
    () => formState.levelId,
    async (newVal, oldVal) => {
        if (!isInitialLoad.value && newVal !== oldVal) {
            formState.connectFlowId = null;
            receiveWhatsappCode.value = false;
        }
        await fetchConnectFlows();
    },
);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const fetchNumber = async (id: number) => {
    try {
        setLoading(true);
        const { data } = await NumberService.get<NumberModel>(id);
        number.value = data;
        formState.levelId = data.level.id;
        receiveWhatsappCode.value = data.whatsappRouting?.isActive === true;
        formState.connectFlowId = data.whatsappRouting?.isActive ? null : data.connectFlowId;
        formState.mediaLabel = data.mediaLabel;
    } catch (err) {
        toastServiceError(err);
    } finally {
        setLoading(false);
    }
};

const fetchLevels = async () => {
    try {
        const { data } = await SelectOptionsService.getLevels();
        levelOptions.value = data;
    } catch (err) {
        toastServiceError(err);
    }
};

const fetchConnectFlows = async () => {
    if (formState.levelId) {
        try {
            const { data } = await SelectOptionsService.getConnectFlows({
                params: {
                    level: formState.levelId,
                },
            });
            connectFlowOptions.value = [...data];
        } catch (err) {
            toastServiceError(err);
        }
    }
};

const onSubmit = async () => {
    const isFormCorrect = await v$.value.$validate();
    if (!isFormCorrect) return;

    if (formState.mediaLabel === '') {
        formState.mediaLabel = null;
    }

    try {
        setLoading(true);
        const id = number.value?.id as number;

        if (receiveWhatsappCode.value) {
            await WhatsappCodeService.activateRouting(id);
            const { levelId, mediaLabel } = formState;
            const { data } = await NumberService.update<DefaultServiceResult>(id, {
                levelId,
                mediaLabel,
            });
            toast.success(data.message);
        } else {
            const { data } = await NumberService.update<DefaultServiceResult>(id, formState);
            toast.success(data.message);
        }

        v$.value.$reset();
        goBack({ name: 'ViewNumberById', params: { id } });
    } catch (err) {
        toastServiceError(err);
    } finally {
        setLoading(false);
    }
};

onMounted(async () => {
    await fetchNumber(Number(route.params.id));
    await fetchLevels();

    if (formState.levelId) {
        await fetchConnectFlows();
    }

    isInitialLoad.value = false;
});

return (_ctx: any,_cache: any) => {
  const _component_UiBackButton = _resolveComponent("UiBackButton")!
  const _component_UiButton = _resolveComponent("UiButton")!
  const _component_UiPageHeader = _resolveComponent("UiPageHeader")!
  const _component_UiMultiselect = _resolveComponent("UiMultiselect")!
  const _component_UiTextInput = _resolveComponent("UiTextInput")!
  const _component_UiCheckboxInput = _resolveComponent("UiCheckboxInput")!
  const _component_UiPanel = _resolveComponent("UiPanel")!
  const _component_UiCheckDirtyBeforeRouteLeave = _resolveComponent("UiCheckDirtyBeforeRouteLeave")!
  const _directive_tippy = _resolveDirective("tippy")!

  return (_openBlock(), _createElementBlock("form", {
    class: "space-y-4 p-4 sm:p-6 lg:p-8",
    autocomplete: "off",
    onSubmit: _withModifiers(onSubmit, ["prevent"])
  }, [
    _createVNode(_component_UiPageHeader, null, {
      info: _withCtx(() => [
        _createElementVNode("h1", _hoisted_1, _toDisplayString(_ctx.$t('number.edit.title')), 1)
      ]),
      actions: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_UiBackButton, { fallbackTo: { name: 'ListNumber' } }),
          (_ctx.$can(_unref(Permission).EDIT_NUMBER))
            ? (_openBlock(), _createBlock(_component_UiButton, {
                key: 0,
                type: "submit",
                variant: "primary",
                "text-variant": "white"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('core.actions.Save')), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }),
    _createVNode(_component_UiPanel, null, {
      default: _withCtx(() => [
        (number.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createElementVNode("div", null, [
                _createElementVNode("label", _hoisted_4, _toDisplayString(_ctx.$t('core.entity.Level')), 1),
                _createVNode(_component_UiMultiselect, {
                  modelValue: formState.levelId,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formState.levelId) = $event)),
                  name: "level",
                  label: "name",
                  "value-prop": "id",
                  primitive: "",
                  options: levelOptions.value
                }, null, 8, ["modelValue", "options"])
              ]),
              _createVNode(_component_UiTextInput, {
                "model-value": number.value ? _unref(applyPhoneMask)(number.value.number) : '',
                name: "mediaLabel",
                type: "text",
                label: _ctx.$t('number.labels.number'),
                disabled: ""
              }, null, 8, ["model-value", "label"]),
              _createVNode(_component_UiTextInput, {
                "model-value": `${number.value?.city} / ${number.value?.state}`,
                name: "mediaLabel",
                type: "text",
                label: _ctx.$t('number.labels.city'),
                disabled: ""
              }, null, 8, ["model-value", "label"]),
              _createVNode(_component_UiTextInput, {
                "model-value": _ctx.$t(`number.types.${number.value?.type}`),
                name: "mediaLabel",
                type: "text",
                label: _ctx.$t('number.labels.type'),
                disabled: ""
              }, null, 8, ["model-value", "label"]),
              _createElementVNode("div", null, [
                _createElementVNode("label", _hoisted_5, _toDisplayString(_ctx.$t('core.entity.ConnectFlow')), 1),
                _createVNode(_component_UiMultiselect, {
                  modelValue: formState.connectFlowId,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formState.connectFlowId) = $event)),
                  name: "connectFlow",
                  label: "name",
                  "value-prop": "id",
                  primitive: "",
                  options: connectFlowOptions.value,
                  "can-clear": "",
                  disabled: receiveWhatsappCode.value
                }, null, 8, ["modelValue", "options", "disabled"]),
                _createElementVNode("div", _hoisted_6, [
                  _createVNode(_component_UiCheckboxInput, {
                    modelValue: receiveWhatsappCode.value,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((receiveWhatsappCode).value = $event)),
                    name: "receiveWhatsappCode",
                    label: _ctx.$t('number.labels.whatsappRoutingOption'),
                    disabled: !canAssignWhatsappCode.value
                  }, {
                    icon: _withCtx(() => [
                      _createVNode(SVGWhatsApp, { class: "h-4 w-4 text-[#25D366]" })
                    ]),
                    _: 1
                  }, 8, ["modelValue", "label", "disabled"]),
                  _withDirectives((_openBlock(), _createElementBlock("span", _hoisted_7, [
                    _createVNode(_unref(QuestionMarkCircleIcon), {
                      class: "h-4 w-4 cursor-help",
                      "aria-hidden": "true"
                    })
                  ])), [
                    [_directive_tippy, { content: _ctx.$t('number.tooltips.whatsappRoutingOption') }]
                  ])
                ])
              ]),
              _createVNode(_component_UiTextInput, {
                modelValue: formState.mediaLabel,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((formState.mediaLabel) = $event)),
                name: "mediaLabel",
                type: "text",
                label: _ctx.$t('number.labels.mediaLabel'),
                errors: _unref(v$).mediaLabel?.$errors,
                onBlur: _unref(v$).mediaLabel?.$touch
              }, null, 8, ["modelValue", "label", "errors", "onBlur"])
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createVNode(_component_UiCheckDirtyBeforeRouteLeave, {
      dirty: _unref(v$).$anyDirty
    }, null, 8, ["dirty"])
  ], 32))
}
}

})